import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAuth from './useAuth';
import { useUsageTracker } from './usageTracker';

export const PARTNER_HOME = '/partner-samples';
// a PSR vendor's account (TS, MS — Jay 01/11) has the Production Status screen instead
export const PSR_HOME = '/production-status';
export const DRIVER_HOME = '/driver-log';
// The partner's handbook opens inside the portal too (task #99, 30/10), so it is the one other page a partner may stand on.
export const PARTNER_MANUAL = '/manual/partner';
const DRIVERLOG_ON = process.env.REACT_APP_DRIVERLOG === '1';

const ProtectedRoute = ({ children, requiredRole, requiredFunction }) => {
  const { auth, role, functions, loading, external, driverOnly } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();
  // Every authenticated page reports usage; public pages (login, ticket token) never mount this.
  useUsageTracker(`${location.pathname}${location.search}`, auth && !loading);

  if (loading) {
    return <div>{t('xAuth.loading')}</div>;
  }

  if (!auth) {
    // Redirect to login if not authenticated, saving the attempted location
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} state={{ from: location }} replace />;
  }

  /* A PARTNER'S ACCOUNT HAS ONE SCREEN (Jay, 2026-10-17, DAC VN). The server refuses it every other route
     anyway; sending it here keeps it from landing on a page whose every call answers 403 — the home page
     and the logo included. */
  if (external) {
    const home = external.track === 'psr' ? PSR_HOME : PARTNER_HOME;
    const allowed = external.track === 'psr' ? [PSR_HOME] : [PARTNER_HOME, PARTNER_MANUAL];
    if (!allowed.includes(location.pathname)) return <Navigate to={home} replace />;
  }

  /* A DRIVER-ONLY ACCOUNT HAS ONE SCREEN TOO (the Driver role, Max 2026-10-19) — the same reasoning: the server
     refuses it everything else, so the home page would be a page of 403s on a phone. */
  if (DRIVERLOG_ON && driverOnly && location.pathname !== DRIVER_HOME) {
    return <Navigate to={DRIVER_HOME} replace />;
  }

  if (requiredRole && role !== requiredRole) {
    // Legacy hard role gate (kept for backward compatibility)
    return <Navigate to="/unauthorized" replace />;
  }

  if (requiredFunction) {
    // Permission gate: allow if the user's role grants ANY of the required function(s).
    const needed = Array.isArray(requiredFunction) ? requiredFunction : [requiredFunction];
    const allowed = needed.some((fn) => functions.includes(fn));
    if (!allowed) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return React.cloneElement(children, { role, functions });
};

export default ProtectedRoute;
